import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { getCaptchaToken } from "./captcha";
import type { LeaderboardGateway, SubmitRunResult } from "./leaderboardAdapter";
import {
  type LeaderboardEntry,
  listEntries as coreListEntries,
  mapRowToEntry,
  normalizePlayerName,
  validateCompletedRun,
  LeaderboardNotFoundError,
  LeaderboardUnavailableError,
  LeaderboardValidationError,
} from "./core";

export interface SupabaseGatewayOptions {
  readonly supabaseUrl?: string;
  readonly publishableKey?: string;
  readonly client?: SupabaseClient;
  readonly getHostname?: () => string;
  /** Swappable so a test never loads the CAPTCHA widget. */
  readonly getCaptchaToken?: () => Promise<string | undefined>;
}

/** Name of the edge function that owns every write to the board. */
const FUNCTION_NAME = "leaderboard";

export function getEnvironmentFromHostname(
  hostname: string,
): "development" | "production" {
  return hostname === "localhost" || hostname === "127.0.0.1"
    ? "development"
    : "production";
}

/**
 * Talks to Supabase.
 *
 * Reads go straight to the table — the board is public. Writes go through the
 * `leaderboard` edge function, because the table no longer grants insert or
 * update to anyone holding the publishable key. The function needs a caller,
 * so the gateway opens an anonymous session first.
 */
export function createSupabaseLeaderboardGateway(
  options: SupabaseGatewayOptions = {},
): LeaderboardGateway {
  const url =
    options.supabaseUrl ??
    (import.meta.env.VITE_SUPABASE_URL as string | undefined);
  const key =
    options.publishableKey ??
    (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined);

  let client: SupabaseClient | undefined = options.client;

  if (!client && url && key) {
    client = createClient(url, key, {
      auth: {
        /**
         * The identity has to outlive the tab, not just the run.
         *
         * Without this every page load minted a fresh anonymous user, which
         * quietly emptied both controls the write path is built on: the
         * function's per-caller hourly limit reset on every reload, and the
         * `user_id` it stamps identified a page load rather than a player, so
         * there was nothing meaningful to revoke. The token is a low-value
         * anonymous credential; storing it buys a caller worth counting.
         */
        persistSession: true,
        // A stored session outlives its access token, so it has to be able to
        // renew — otherwise a tab left open simply stops being able to save.
        autoRefreshToken: true,
      },
    });
  }

  const getEnv = (): "development" | "production" => {
    if (options.getHostname) {
      return getEnvironmentFromHostname(options.getHostname());
    }
    if (typeof window !== "undefined" && window.location) {
      return getEnvironmentFromHostname(window.location.hostname);
    }
    return "production";
  };

  const captcha = options.getCaptchaToken ?? getCaptchaToken;

  const require = (): SupabaseClient => {
    if (!client) {
      throw new LeaderboardUnavailableError();
    }
    return client;
  };

  /**
   * An anonymous session, created once and reused.
   *
   * The project has to allow anonymous sign-ins; without it there is no caller
   * the function will accept, and the board degrades to read-only.
   */
  let session: Promise<void> | null = null;
  const signIn = (db: SupabaseClient, force = false): Promise<void> => {
    if (force) {
      session = null;
    }
    session ??= (async () => {
      const { data } = await db.auth.getSession();
      if (data.session && !force) {
        return;
      }
      // Only asked for when the project is configured to want one; without a
      // site key this resolves to undefined and the call is unchanged.
      const captchaToken = await captcha();
      const { error } = await db.auth.signInAnonymously(
        captchaToken ? { options: { captchaToken } } : undefined,
      );
      if (error) {
        session = null;
        throw new LeaderboardUnavailableError();
      }
    })();
    return session;
  };

  const statusOf = (error: unknown): number | undefined =>
    (error as { context?: { status?: number } }).context?.status;

  /** Map the function's status codes onto the errors the adapter knows. */
  const callFunction = async (
    db: SupabaseClient,
    method: "POST" | "PATCH",
    body: Record<string, unknown>,
  ): Promise<{ outcome: string; entry: unknown }> => {
    await signIn(db);
    let { data, error } = await db.functions.invoke(FUNCTION_NAME, {
      method,
      body,
    });

    // A stored identity can outlive the account behind it — the user may have
    // been cleaned up, or the project's auth reset. Retried once with a fresh
    // sign-in, because otherwise persisting the session would strand a player
    // on a token nothing will ever accept again.
    if (error && statusOf(error) === 401) {
      await signIn(db, true);
      ({ data, error } = await db.functions.invoke(FUNCTION_NAME, {
        method,
        body,
      }));
    }

    if (error) {
      const status = statusOf(error);
      if (status === 400) {
        throw new LeaderboardValidationError();
      }
      if (status === 404) {
        throw new LeaderboardNotFoundError();
      }
      throw new LeaderboardUnavailableError({ rateLimited: status === 429 });
    }
    return data as { outcome: string; entry: unknown };
  };

  return {
    async submitRun(input): Promise<SubmitRunResult> {
      const db = require();
      // Validated here as well so an impossible payload never leaves the tab;
      // the function does not trust this and checks again.
      const run = validateCompletedRun(input);

      // No `environment` here on purpose: the function derives it from the
      // request's `Origin`, which page script cannot forge. Sending one would
      // only suggest the caller still gets a say. Reads below still use the
      // hostname — the same page, so the same answer.
      const result = await callFunction(db, "POST", { ...run });
      const entry = mapRowToEntry(result.entry);

      // The board ranks by score, and only the leading page is fetched, so the
      // rank has to be read back rather than inferred from the insert.
      const rows = await coreListEntries(db, { environment: getEnv() });
      const index = rows.entries.findIndex((row) => row.id === entry.id);

      return {
        outcome: result.outcome === "duplicate" ? "duplicate" : "saved",
        submittedRank: index === -1 ? null : index + 1,
        submittedEntry: entry,
      };
    },

    async listEntries(): Promise<readonly LeaderboardEntry[]> {
      const res = await coreListEntries(require(), { environment: getEnv() });
      return res.entries;
    },

    async updatePlayerName(
      runId: string,
      playerName: string,
    ): Promise<unknown> {
      const db = require();
      return callFunction(db, "PATCH", {
        runId,
        playerName: normalizePlayerName(playerName),
      });
    },
  };
}
